import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Slider,
  Divider,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import { safeJSONParse } from "../../../utils/helper";
import styles from "../agent-response.module.scss";

type Weight = {
  key: string;
  label: string;
  weight: number;
};

type RequirementData = {
  category: string;
  service_type: string;
  capabilities: string[];
  qualifications: string[];
  source: string;
  raw_input_summary: string;
  uploaded_filename?: string | null;
};

type SupplierDiscoveryData = {
  agent_id: string;
  agent_name: string;
  requirement: RequirementData;
  weights: Weight[];
  editable_fields: string[];
  actions: string[];
};

type SupplierDiscoveryRequirementReviewProps = {
  parsedContent: any;
  messageId?: string;
};

export default function SupplierDiscoveryRequirementReview({
  parsedContent,
  messageId,
}: SupplierDiscoveryRequirementReviewProps): JSX.Element {
  // Redux hooks
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );

  // Parse the finalAnswer output
  const finalAnswer = parsedContent?.finalAnswer || {};
  const outputData: SupplierDiscoveryData = safeJSONParse(
    finalAnswer?.output || "{}",
  );

  // State management - must be declared before use
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Check if this is the latest message to determine if form should be interactive
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const latestAgentMessage = [...currentChat]
    .reverse()
    .find((msg) => msg.sender === "agent");
  const isLatestMessage = latestAgentMessage?.id === messageId;

  // If not the latest message, everything should be disabled (read-only mode)
  const isDisabled = !isLatestMessage || isConfirming;
  const [category, setCategory] = useState(
    outputData.requirement?.category || "",
  );
  const [serviceType, setServiceType] = useState(
    outputData.requirement?.service_type || "",
  );
  const [capabilities, setCapabilities] = useState<string[]>(
    outputData.requirement?.capabilities || [],
  );
  const [qualifications, setQualifications] = useState<string[]>(
    outputData.requirement?.qualifications || [],
  );
  const [weights, setWeights] = useState<Weight[]>(outputData.weights || []);
  const [capabilityInput, setCapabilityInput] = useState("");
  const [qualificationInput, setQualificationInput] = useState("");

  // Handle weight adjustments
  const handleWeightChange = (key: string, newValue: number) => {
    setWeights((prev) =>
      prev.map((w) => (w.key === key ? { ...w, weight: newValue } : w)),
    );
  };

  // Normalize weights to sum to 100
  const normalizeWeights = () => {
    const total = weights.reduce((sum, w) => sum + w.weight, 0);
    if (total === 0) return;
    setWeights((prev) =>
      prev.map((w) => ({
        ...w,
        weight: Math.round((w.weight / total) * 100),
      })),
    );
  };

  // Handle capability addition
  const handleAddCapability = () => {
    const trimmedValue = capabilityInput.trim();
    if (trimmedValue && capabilities.indexOf(trimmedValue) === -1) {
      setCapabilities((prev) => [...prev, trimmedValue]);
      setCapabilityInput("");
    }
  };

  // Handle qualification addition
  const handleAddQualification = () => {
    const trimmedValue = qualificationInput.trim();
    if (trimmedValue && qualifications.indexOf(trimmedValue) === -1) {
      setQualifications((prev) => [...prev, trimmedValue]);
      setQualificationInput("");
    }
  };

  // Remove capability
  const removeCapability = (cap: string) => {
    setCapabilities((prev) => prev.filter((c) => c !== cap));
  };

  // Remove qualification
  const removeQualification = (qual: string) => {
    setQualifications((prev) => prev.filter((q) => q !== qual));
  };

  // Constants - must be declared before functions that use them
  const editableFields = outputData.editable_fields || [];
  const requirement = outputData.requirement || {};
  const totalWeightSum = weights.reduce((sum, w) => sum + w.weight, 0);

  // Helper to check if a field is editable
  const isFieldEditable = (fieldName: string): boolean => {
    return editableFields.indexOf(fieldName) !== -1;
  };

  // Build modify_array with changed fields - memoized for performance
  const buildModifyArray = useCallback(() => {
    const modifyArray: Array<{ key: string; value: any }> = [];
    const originalData = outputData.requirement;
    const originalWeights = outputData.weights;

    // Check category change
    if (category !== originalData?.category) {
      modifyArray.push({ key: "category", value: category });
    }

    // Check capabilities change
    const capabilitiesChanged =
      capabilities.length !== (originalData?.capabilities || []).length ||
      capabilities.some(
        (cap, idx) => cap !== originalData?.capabilities?.[idx],
      );

    if (capabilitiesChanged) {
      modifyArray.push({ key: "capabilities", value: capabilities });
    }

    // Check qualifications change
    const qualificationsChanged =
      qualifications.length !== (originalData?.qualifications || []).length ||
      qualifications.some(
        (qual, idx) => qual !== originalData?.qualifications?.[idx],
      );

    if (qualificationsChanged) {
      modifyArray.push({ key: "qualifications", value: qualifications });
    }

    // Check weights change
    const weightsChanged = weights.some(
      (weight, idx) => weight.weight !== originalWeights?.[idx]?.weight,
    );

    if (weightsChanged) {
      modifyArray.push({ key: "weights", value: weights });
    }

    return modifyArray;
  }, [category, capabilities, qualifications, weights, outputData]);

  // Format user-friendly display message for the chat UI
  const formatUserMessage = useCallback(() => {
    const modifyArray = buildModifyArray();

    // If no changes, show simple message
    if (modifyArray.length === 0) {
      return `✓ Confirmed supplier search criteria

Category: ${category}
Capabilities: ${capabilities.join(", ")}
Qualifications: ${qualifications.join(", ")}`;
    }

    // Build changes summary
    const parts: string[] = ["✓ Confirmed with modifications:"];

    modifyArray.forEach(change => {
      switch (change.key) {
        case "category":
          parts.push(`• Category: ${change.value}`);
          break;
        case "capabilities":
          parts.push(`• Capabilities: ${change.value.join(", ")}`);
          break;
        case "qualifications":
          parts.push(`• Qualifications: ${change.value.join(", ")}`);
          break;
        case "weights":
          parts.push(`• Search weights: Adjusted`);
          break;
      }
    });

    return parts.join("\n");
  }, [category, capabilities, qualifications, buildModifyArray]);

  // Handle confirm action - memoized to prevent recreation
  const handleConfirm = useCallback(() => {
    if (isConfirming) return; // Prevent multiple clicks

    setIsConfirming(true);

    const modifyArray = buildModifyArray();
    const userMessage = formatUserMessage();

    // Get sessionId from URL params if available
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("sessionId");

    // Send message to agent - message param is what shows in UI
    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || 0,
        message: userMessage, // ← This is what shows in the chat UI
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent?.endpoint || "/stream/supplier-discovery",
        sessionId,
        modify_array: modifyArray, // ← Backend data, should not affect UI
      }),
    );
  }, [isConfirming, currentAgent, dispatch, buildModifyArray, formatUserMessage]);

  return (
    <Box className={styles.pressReleaseContent}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: "#f8fffe",
          border: "2px solid #00d2aa",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <SearchIcon sx={{ fontSize: 32, color: "#00d2aa" }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ color: "#004141", fontWeight: 600, mb: 0.5 }}
            >
              {outputData.agent_name ||
                "Supplier Discovery — Requirement Review"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Review the extracted requirements and adjust search criteria
            </Typography>
          </Box>
        </Box>

        {/* RFQ Summary */}
        {requirement.raw_input_summary && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{
              backgroundColor: "#e3f2fd",
              color: "#1565c0",
              "& .MuiAlert-icon": { color: "#1565c0" },
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
            >
              Document Source{requirement.uploaded_filename && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ fontWeight: 400, opacity: 0.8 }}
                >
                  {" — "}{requirement.uploaded_filename}
                </Typography>
              )}
            </Typography>
            <Typography variant="body2">
              {requirement.raw_input_summary}
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Main Content */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
        }}
      >
        {/* Category Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: "#004141", fontWeight: 600, mb: 2 }}
          >
            Category
          </Typography>

          {isEditing && isFieldEditable("category") && !isDisabled ? (
            <TextField
              fullWidth
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Enter category (e.g., IT Security Services)"
              size="small"
              disabled={isDisabled}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  "& fieldset": { borderColor: "#d1d5db" },
                  "&:hover fieldset": { borderColor: "#00d2aa" },
                  "&.Mui-focused fieldset": { borderColor: "#00d2aa" },
                },
              }}
            />
          ) : (
            <Chip
              label={category || "Not specified"}
              sx={{
                backgroundColor: "#e6f9f5",
                color: "#00a884",
                fontWeight: 600,
                fontSize: "14px",
                height: 32,
              }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Service Type Section */}
        {serviceType && (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: "#004141", fontWeight: 600, mb: 2 }}
              >
                Service Type
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#403f3f",
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: "13px",
                  }}
                >
                  {serviceType}
                </Typography>
              </Paper>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </>
        )}

        {/* Capabilities Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ color: "#004141", fontWeight: 600 }}
            >
              Required Capabilities
            </Typography>
            <Tooltip title="Technical capabilities and services required">
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "#999" }} />
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {capabilities.map((cap, idx) => (
              <Chip
                key={idx}
                label={cap}
                onDelete={
                  isEditing && isFieldEditable("capabilities") && !isDisabled
                    ? () => removeCapability(cap)
                    : undefined
                }
                sx={{
                  backgroundColor: "#dcfada",
                  color: "#267103",
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>

          {isEditing && isFieldEditable("capabilities") && !isDisabled && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                value={capabilityInput}
                onChange={(e) => setCapabilityInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddCapability();
                  }
                }}
                placeholder="Add capability (e.g., penetration testing)"
                size="small"
                disabled={isDisabled}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    "& fieldset": { borderColor: "#d1d5db" },
                    "&:hover fieldset": { borderColor: "#00d2aa" },
                    "&.Mui-focused fieldset": { borderColor: "#00d2aa" },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddCapability}
                disabled={isDisabled}
                sx={{
                  backgroundColor: "#00d2aa",
                  "&:hover": { backgroundColor: "#00b894" },
                  "&:disabled": { backgroundColor: "#ccc" },
                }}
              >
                Add
              </Button>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Qualifications Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ color: "#004141", fontWeight: 600 }}
            >
              Required Qualifications
            </Typography>
            <Tooltip title="Certifications and compliance requirements">
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "#999" }} />
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {qualifications.map((qual, idx) => (
              <Chip
                key={idx}
                label={qual}
                onDelete={
                  isEditing && isFieldEditable("qualifications") && !isDisabled
                    ? () => removeQualification(qual)
                    : undefined
                }
                sx={{
                  backgroundColor: "#e3f2fd",
                  color: "#1565c0",
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>

          {isEditing && isFieldEditable("qualifications") && !isDisabled && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                value={qualificationInput}
                onChange={(e) => setQualificationInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddQualification();
                  }
                }}
                placeholder="Add qualification (e.g., ISO 27001)"
                size="small"
                disabled={isDisabled}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    "& fieldset": { borderColor: "#d1d5db" },
                    "&:hover fieldset": { borderColor: "#00d2aa" },
                    "&.Mui-focused fieldset": { borderColor: "#00d2aa" },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddQualification}
                disabled={isDisabled}
                sx={{
                  backgroundColor: "#00d2aa",
                  "&:hover": { backgroundColor: "#00b894" },
                  "&:disabled": { backgroundColor: "#ccc" },
                }}
              >
                Add
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Weight Adjustment Section */}
      {isFieldEditable("weights") && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#ffffff",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <TuneIcon sx={{ color: "#00d2aa" }} />
            <Typography
              variant="subtitle1"
              sx={{ color: "#004141", fontWeight: 600 }}
            >
              Search Criteria Weights
            </Typography>
          </Box>

          {isEditing && !isDisabled ? (
            <>
              {totalWeightSum !== 100 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Weights sum to {totalWeightSum}%. Click "Normalize" to adjust
                  to 100%.
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                {weights.map((weight) => (
                  <Box key={weight.key} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#333", fontWeight: 500 }}
                      >
                        {weight.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#00d2aa", fontWeight: 600 }}
                      >
                        {weight.weight}%
                      </Typography>
                    </Box>
                    <Slider
                      value={weight.weight}
                      onChange={(_, newValue) =>
                        handleWeightChange(weight.key, newValue as number)
                      }
                      min={0}
                      max={100}
                      step={5}
                      marks
                      disabled={isDisabled}
                      sx={{
                        color: "#00d2aa",
                        "& .MuiSlider-thumb": {
                          backgroundColor: "#00d2aa",
                        },
                        "& .MuiSlider-track": {
                          backgroundColor: "#00d2aa",
                        },
                        "& .MuiSlider-rail": {
                          backgroundColor: "#e0e0e0",
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {totalWeightSum !== 100 && !isDisabled && (
                <Button
                  variant="outlined"
                  onClick={normalizeWeights}
                  fullWidth
                  disabled={isDisabled}
                  sx={{
                    color: "#00d2aa",
                    borderColor: "#00d2aa",
                    "&:hover": {
                      borderColor: "#00b894",
                      backgroundColor: "#f8fffe",
                    },
                    "&:disabled": {
                      borderColor: "#ccc",
                      color: "#ccc",
                    },
                  }}
                >
                  Normalize Weights to 100%
                </Button>
              )}
            </>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {weights.map((weight) => (
                <Box key={weight.key} sx={{ flex: "1 1 45%" }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#666", display: "block", mb: 0.5 }}
                  >
                    {weight.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: "#00d2aa", fontWeight: 600 }}
                  >
                    {weight.weight}%
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* Action Buttons - Only show if this is the latest message */}
      {isLatestMessage && (
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          {isEditing ? (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  // Reset to original values
                  setCategory(outputData.requirement?.category || "");
                  setServiceType(outputData.requirement?.service_type || "");
                  setCapabilities(outputData.requirement?.capabilities || []);
                  setQualifications(outputData.requirement?.qualifications || []);
                  setWeights(outputData.weights || []);
                  setIsEditing(false);
                }}
                disabled={isDisabled}
                sx={{
                  color: "#666",
                  borderColor: "#d1d5db",
                  "&:hover": { borderColor: "#999" },
                  "&:disabled": { borderColor: "#ccc", color: "#ccc" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setIsEditing(false)}
                disabled={isDisabled}
                sx={{
                  backgroundColor: "#00d2aa",
                  "&:hover": { backgroundColor: "#00b894" },
                  "&:disabled": { backgroundColor: "#ccc", color: "#fff" },
                }}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              {editableFields.length > 0 && !isDisabled && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditing(true)}
                  disabled={isDisabled}
                  sx={{
                    color: "#00d2aa",
                    borderColor: "#00d2aa",
                    "&:hover": {
                      borderColor: "#00b894",
                      backgroundColor: "#f8fffe",
                    },
                    "&:disabled": {
                      borderColor: "#ccc",
                      color: "#ccc",
                    },
                  }}
                >
                  Edit Requirements
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleConfirm}
                disabled={isDisabled}
                sx={{
                  backgroundColor: "#00d2aa",
                  "&:hover": { backgroundColor: "#00b894" },
                  "&:disabled": { backgroundColor: "#b2e5de", color: "#fff" },
                  px: 4,
                }}
              >
                {isConfirming ? "Searching..." : "Confirm & Search Suppliers"}
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Footer Prompt */}
      {finalAnswer.prompt && (
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              fontStyle: "italic",
              textAlign: "center",
              p: 2,
              backgroundColor: "#f8f9fa",
              borderRadius: 1,
            }}
          >
            {finalAnswer.prompt}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
